import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { mergeExistingFields } from "helpers/mergeExistingFields";

import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import sanitizeFileName from "helpers/sanitizeFileName";
import { ApiErrorPayload } from "types/ApiErrorPayload";
import { Translation } from "../types";
import {
  getChangedFields,
  normalizeOptionalText,
} from "helpers/getChangedFields";
import { normalizeSeoHtml } from "helpers/seoAttributes";
import type { RootState } from "store";
import {
  applyProviderCurrencyCapability,
  readProviderCurrencyCapability,
} from "./providerCurrencyState";

export interface ProviderCurrencyState {
  currency: string;
  providerSupported: boolean;
  managerEnabled: boolean;
  effectiveActive: boolean;
  effectiveStatus: string;
  isActive: boolean;
  isPriority: boolean;
}

interface ProviderItem {
  id: number;
  title: string;
  description: string;
  slug: string;
  isActive: string;
  isPopular?: boolean;
  image: string | File;
  countOfGames?: number;
  icon?: string | File;
  translations: Array<Translation>;
  currencies?: string;
  geoRestrictions?: string[];
  providerCurrencies?: ProviderCurrencyState[];
}
export interface ProvidersBunchPayload {
  ids: number[];
  isActive?: number;
  isPopular?: number;
}
interface ProvidersState {
  list: ProviderItem[];
  activePage: number;
  totalPages: number;
  totalItems: number;
  isListFetched: boolean;
  isItemFetched: boolean;
  isItemLocaleFetching: boolean;
  selectedItems: number[];
  payload?: ProviderItem;
  initialPayload?: ProviderItem;
  isItemFetching: boolean;
  processingItemId?: ProviderItem["id"];
  currencyMutationSeq: number;
}
interface GetListPayload {
  limit: number;
  offset: number;
  isActive?: number;
  title: string;
  order: {
    title: string;
    slug: string;
    countOfGames: string;
  };
}

export const getProvidersList = createAsyncThunk(
  "game-center/providers/list",
  async (data: GetListPayload) => {
    try {
      const result = await instance.post(`${apiRoutes.providersList}`, {
        ...data,
      });
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const updateProvider = createAsyncThunk<
  boolean,
  ProviderItem,
  { rejectValue: ApiErrorPayload }
>(
  "game-center/providers/update",
  async (payload: ProviderItem, { rejectWithValue }) => {
    const data = new FormData();
    if (payload.image instanceof File) {
      const safeName = sanitizeFileName(payload.image.name);

      data.append(
        "image",
        new File([payload.image], safeName, { type: payload.image.type }),
      );
    }

    if (payload.icon instanceof File) {
      const safeName = sanitizeFileName(payload.icon.name);

      data.append(
        "icon",
        new File([payload.icon], safeName, { type: payload.icon.type }),
      );
    }

    data.append("title", (payload?.title as string) ?? "");
    data.append("slug", (payload.slug as string) ?? "");
    data.append("description", (payload.description as string) ?? "");
    data.append("isActive", payload.isActive ? "1" : "0");
    data.append("isPopular", payload.isPopular ? "1" : "0");

    try {
      await instance.put(`${apiRoutes.providerUpdate}/${payload.id}`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return true;
    } catch (error: any) {
      const data = error?.data;
      const apiError = data?.error ?? data;

      if (apiError && apiError.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);

export const updateProvidersBunch = createAsyncThunk(
  "game-center/providers/update-bunch",
  async (data: ProvidersBunchPayload) => {
    try {
      await instance.put(`${apiRoutes.updateProvidersBunch}`, {
        ...data,
      });
      return true;
    } catch (error) {
      return false;
    }
  },
);

export const updateProviderCurrency = createAsyncThunk(
  "game-center/providers/update-currency",
  async ({
    id,
    currency,
    isActive,
    managerEnabled,
    isPriority,
    requestSeq,
  }: {
    id: number;
    currency: string;
    isActive?: boolean;
    managerEnabled?: boolean;
    isPriority?: boolean;
    requestSeq: number;
  }) => {
    const body: {
      currency: string;
      isActive?: boolean;
      managerEnabled?: boolean;
      isPriority?: boolean;
    } = { currency };
    if (isActive !== undefined) {
      body.isActive = isActive;
    }
    if (managerEnabled !== undefined) {
      body.managerEnabled = managerEnabled;
    }
    if (isPriority !== undefined) {
      body.isPriority = isPriority;
    }
    const result = await instance.post(`${apiRoutes.providerItem}/${id}/currency-manager`, body);
    const state = readProviderCurrencyCapability(result);
    if (!state) {
      throw new Error("Provider currency response is missing isActive");
    }
    return {
      state,
      requestSeq,
      providerId: id,
    };
  },
);

export const getProviderItem = createAsyncThunk(
  "game-center/providers/read",
  async (id: string | number) => {
    try {
      const result = await instance.get(`${apiRoutes.providerItem}/${id}`);
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const deleteProviderItem = createAsyncThunk(
  "game-center/providers/delete",
  async (id: string | number) => {
    try {
      const result = await instance.delete(`${apiRoutes.providerDelete}/${id}`);
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const getProviderItemLocale = createAsyncThunk(
  "game-center/providers/get-item-locale",
  async ({ id, locale }: { id: string | number; locale: string }) => {
    try {
      const result = await instance.get(
        `${apiRoutes.providerItem}/${id}/translation/${locale}`,
      );
      return true;
    } catch (error) {
      return false;
    }
  },
);

export const updateProviderItemLocale = createAsyncThunk(
  "game-center/providers/update-item-locale",
  async (
    { id, locale }: { id: string | number; locale: Partial<Translation> },
    { getState },
  ) => {
    try {
      const { locale: localeName, ...restData } = locale;
      const initialTranslation = (
        getState() as RootState
      ).providers.initialPayload?.translations?.find(
        (translation) => translation.locale === localeName,
      );
      const dataToUpdate = getChangedFields(
        initialTranslation,
        restData,
        ["title", "description", "isActive"],
        {
          title: normalizeOptionalText,
          description: normalizeSeoHtml,
          isActive: Boolean,
        },
      );

      await instance.put(
        `${apiRoutes.providerItem}/${id}/translation/${localeName}`,
        dataToUpdate,
      );
      return true;
    } catch (error) {
      return false;
    }
  },
);

const initialState: ProvidersState = {
  list: [],
  isListFetched: false,
  isItemFetched: false,
  isItemLocaleFetching: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  selectedItems: [],
  isItemFetching: false,
  currencyMutationSeq: 0,
};

export const providersSlice = createSlice({
  name: "providers",
  initialState,
  reducers: {
    handlePaginate: (state: ProvidersState, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },
    markProviderListStale: (state: ProvidersState) => {
      state.isListFetched = false;
    },
    handleUpdateFields: (
      state: ProvidersState,
      action: PayloadAction<ProviderItem>,
    ) => {
      state.payload = { ...state.payload, ...action.payload };
    },
    handleClearFields: (state: ProvidersState) => {
      state.payload = undefined;
      state.initialPayload = undefined;
      // state.isListFetched = false;
    },
    handleSelectItem: (
      state: ProvidersState,
      action: PayloadAction<number>,
    ) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItems = [...state.selectedItems, action.payload];
      }
    },
    handleSelectAll: (state: ProvidersState) => {
      if (state.selectedItems.length === state.list.length) {
        state.selectedItems = [];
      } else {
        state.selectedItems = state.list.map((el) => el.id);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getProvidersList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 1;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(updateProvider.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updateProvider.fulfilled, (state, action) => {
      state.list = state.list.map((item) =>
        item.id === action.meta.arg.id
          ? mergeExistingFields(item, {
              ...action.meta.arg,
              image:
                action.meta.arg.image instanceof File
                  ? item.image
                  : action.meta.arg.image,
              icon:
                action.meta.arg.icon instanceof File
                  ? item.icon
                  : action.meta.arg.icon,
            })
          : item,
      );
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(updateProvider.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(updateProvidersBunch.fulfilled, (state) => {
      state.isListFetched = false;
      state.selectedItems = [];
    });
    builder.addCase(getProviderItem.pending, (state) => {
      state.isItemFetched = false;
    });
    builder.addCase(getProviderItem.fulfilled, (state, action) => {
      state.isItemFetched = true;
      state.payload = action.payload;
      state.initialPayload = action.payload
        ? {
            ...action.payload,
            translations: action.payload.translations?.map(
              (translation: Translation) => ({
                ...translation,
              }),
            ),
          }
        : undefined;
    });
    builder.addCase(updateProviderCurrency.fulfilled, (state, action) => {
      const updated = action.payload?.state;
      const requestSeq = action.payload?.requestSeq ?? 0;
      const providerId = action.payload?.providerId;
      if (requestSeq < state.currencyMutationSeq) {
        return;
      }
      if (!state.payload || state.payload.id !== providerId) {
        return;
      }
      state.currencyMutationSeq = requestSeq;
      const nextRows = applyProviderCurrencyCapability(
        state.payload.providerCurrencies,
        updated,
      );
      if (nextRows) {
        state.payload.providerCurrencies = nextRows;
      }

      if (updated?.currency) {
        state.list = state.list.map((item) => {
          if (item.id !== providerId) {
            return item;
          }

          const currencyRows = nextRows ?? state.payload?.providerCurrencies;
          if (
            currencyRows?.length &&
            currencyRows.every((row) => typeof row.effectiveActive === "boolean")
          ) {
            return mergeExistingFields(item, {
              currencies: currencyRows
                .filter((row) => row.effectiveActive)
                .map((row) => row.currency)
                .join(","),
            });
          }

          const shouldInclude =
            typeof updated.effectiveActive === "boolean"
              ? updated.effectiveActive
              : updated.effectiveStatus
                ? updated.effectiveStatus === "active"
                : typeof updated.managerEnabled === "boolean"
                  ? updated.isActive &&
                    updated.managerEnabled &&
                    updated.providerSupported !== false
                  : updated.isActive && updated.providerSupported !== false;
          const currencies = (item.currencies ?? "")
            .split(",")
            .map((currency) => currency.trim())
            .filter(Boolean)
            .filter((currency) => currency !== updated.currency);
          if (shouldInclude) {
            currencies.push(updated.currency);
          }

          return { ...item, currencies: currencies.join(",") };
        });
      }
    });
    builder.addCase(deleteProviderItem.fulfilled, (state) => {
      state.isListFetched = false;
    });
    builder.addCase(getProviderItemLocale.pending, (state) => {
      state.isItemLocaleFetching = true;
    });
    builder.addCase(getProviderItemLocale.fulfilled, (state, action) => {
      state.isItemLocaleFetching = false;
    });
    builder.addCase(getProviderItemLocale.rejected, (state, action) => {
      state.isItemLocaleFetching = false;
    });
    builder.addCase(updateProviderItemLocale.pending, (state) => {
      state.isItemLocaleFetching = true;
      state.isItemFetching = true;
    });
    builder.addCase(updateProviderItemLocale.fulfilled, (state, action) => {
      state.isItemLocaleFetching = false;
      state.isItemFetching = false;
      state.initialPayload = state.payload
        ? {
            ...state.payload,
            translations: state.payload.translations?.map((translation) => ({
              ...translation,
            })),
          }
        : undefined;
    });
    builder.addCase(updateProviderItemLocale.rejected, (state, action) => {
      state.isItemLocaleFetching = false;
      state.isItemFetching = false;
    });
  },
});

export const {
  handlePaginate,
  markProviderListStale,
  handleSelectItem,
  handleSelectAll,
  handleClearFields,
  handleUpdateFields,
} = providersSlice.actions;
